import { type UploadDocumentTarget, useUploadDocumentMutation } from '#api-bridge/documentApi.ts';
import { useUploadLinkMutation } from '#api-bridge/documentLinkApi.ts';
import type { Document } from '#domain/Document.ts';

// Utility function to upload a file to a presigned S3 URL
const uploadFileToPresignedUrl = async ({
    file,
    uploadUrl,
    onProgress,
}: {
    file: File;
    uploadUrl: string;
    onProgress?: (percent: number) => void;
}) => {
    return new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl, true);
        xhr.setRequestHeader('Content-Type', file.type);

        xhr.upload.onprogress = event => {
            if (event.lengthComputable && onProgress) {
                const percent = Math.round((event.loaded / event.total) * 90) + 10;
                onProgress(percent);
            }
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                if (onProgress) {
                    onProgress(100);
                }
                resolve();
            } else {
                reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
            }
        };

        xhr.onerror = () => {
            reject(new Error('Network error during upload'));
        };
        file.arrayBuffer().then(buff => xhr.send(buff));
    });
};

export const useUploadDocument = (target: UploadDocumentTarget) => {
    const [getUploadLink] = useUploadLinkMutation(); // Step 1
    const [uploadMetadata] = useUploadDocumentMutation(); // Step 3

    const upload = async ({ file, associations }: { file: File; associations: Document['associations'] }) => {
        // Step 1
        const id = crypto.randomUUID();
        const { data, error } = await getUploadLink({ documentId: id, fileName: file.name });
        if (error) {
            console.log('Upload link failed:', error);
            throw error;
        }
        const uploadUrl = data.url;
        console.log('Upload link fetched:', uploadUrl);

        // Step 2
        await uploadFileToPresignedUrl({
            file,
            uploadUrl,
            onProgress: percent => console.log(`Upload progress: ${percent}%`),
        });
        console.log('Upload successful');

        // Step 3
        await uploadMetadata({
            list: {
                items: [
                    {
                        type: 'other', // TODO: add dropdown for type selection
                        id,
                        fileName: file.name,
                        name: file.name,
                        origin: 'dispatcher',
                        externalId: '',
                        associations,
                    },
                ],
            },
            ...target,
        }).unwrap();
        console.log('Uploaded metadata');
    };

    return { upload };
};
